import { useEffect, useRef, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { EditorContent } from '@tiptap/react'
import { createVercelAIStream } from '@/lib/streaming-utils'
import { Button } from '@deepguide-ai/ui/button'
import { useAiEditor } from '@/tiptap'
import { aiChangesPluginKey, ReviewChangePopover } from '@/tiptap'
import { SERVER_URL } from '@/lib/env'

// Utility function to create API streams (from ai-demo.tsx)
const createAPIStream = async (
  prompt: { system: string; user: string },
  signal?: AbortSignal
): Promise<ReadableStream<Uint8Array>> => {
  const token = localStorage.getItem('auth:token')
  const workspaceId = localStorage.getItem('auth:activeWorkspaceId')
  const response = await fetch(
    `${SERVER_URL}/api/ai/editor`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'X-Workspace-Id': workspaceId || '',
      },
      body: JSON.stringify({
        messages: [{ role: 'user', content: prompt.user }],
        system: prompt.system,
      }),
      signal,
    }
  )
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)
  if (!response.body) throw new Error('No response body')
  return response.body
}

// Mock responses for different AI commands (from ai-demo.tsx)
const MOCK_RESPONSES = {
  restructure:
    "Here's a restructured version of your content with improved organization and flow. The key points are now arranged in a logical sequence that enhances readability and comprehension.",
  bloggify:
    '# Your Content as a Blog Post\n\nTransforming your ideas into an engaging blog post! This content has been restructured with compelling headlines and smooth transitions that will captivate your readers.',
  keypoints:
    '• Main point one extracted from your content\n• Second important takeaway\n• Third key insight\n• Final crucial point that summarizes the essence',
  rephrase:
    'Your content has been rephrased for improved clarity while maintaining the original meaning. The language is now more accessible and the ideas flow more naturally.',
  shorten:
    'Concise version: Your key message distilled to its essential elements without losing important information.',
  extend:
    'Your original content expanded with additional relevant details, examples, and context. This provides readers with a more comprehensive understanding of the topic while maintaining the original tone and style.',
  summarize:
    'Summary: This is a comprehensive overview that captures all the key points from your content in a concise, easy-to-understand format.',
  simplify:
    'Simplified version: Your content has been made more accessible by using clearer language and breaking down complex concepts into digestible parts.',
  fixSpellingAndGrammar:
    'Your content with all spelling, grammar, and punctuation errors corrected while preserving the original meaning and style.',
  complete:
    "...continuing from where you left off, here's how the content might naturally progress with additional relevant information and insights.",
  emojify:
    'Your content enhanced with appropriate emojis! 😊 This makes it more engaging and expressive! ✨🎉',
  deEmojify:
    'Your content with all emojis removed while maintaining readability and flow.',
  translate:
    'Translated content: This is your text translated to the requested language while maintaining the original tone and meaning.',
  tldr: 'TL;DR: Quick summary of the main points in your content.',
  adjustTone:
    'Content adjusted to match the requested tone while preserving the original meaning and key information.',
  textPrompt:
    'AI response based on your custom instructions and the provided content.',
  default:
    'This is a mock AI response for testing purposes. The actual response would depend on the AI command used.',
}

// Mock stream generator using streaming utilities (from ai-demo.tsx)
const createMockStream = async (
  prompt: { system: string; user: string },
  signal?: AbortSignal
): Promise<ReadableStream<Uint8Array>> => {
  const promptLower = prompt.user.toLowerCase()
  let responseText = MOCK_RESPONSES.default
  if (promptLower.includes('restructure'))
    responseText = MOCK_RESPONSES.restructure
  else if (promptLower.includes('blog')) responseText = MOCK_RESPONSES.bloggify
  else if (promptLower.includes('key points'))
    responseText = MOCK_RESPONSES.keypoints
  else if (promptLower.includes('rephrase'))
    responseText = MOCK_RESPONSES.rephrase
  else if (promptLower.includes('shorten'))
    responseText = MOCK_RESPONSES.shorten
  else if (promptLower.includes('expand') || promptLower.includes('extend'))
    responseText = MOCK_RESPONSES.extend
  else if (promptLower.includes('summarize'))
    responseText = MOCK_RESPONSES.summarize
  else if (promptLower.includes('simplify'))
    responseText = MOCK_RESPONSES.simplify
  else if (promptLower.includes('spelling') || promptLower.includes('grammar'))
    responseText = MOCK_RESPONSES.fixSpellingAndGrammar
  else if (promptLower.includes('continue') || promptLower.includes('complete'))
    responseText = MOCK_RESPONSES.complete
  else if (promptLower.includes('emoji') && !promptLower.includes('de-emoji'))
    responseText = MOCK_RESPONSES.emojify
  else if (promptLower.includes('de-emoji'))
    responseText = MOCK_RESPONSES.deEmojify
  else if (promptLower.includes('translate'))
    responseText = MOCK_RESPONSES.translate
  else if (promptLower.includes('tldr') || promptLower.includes('tl;dr'))
    responseText = MOCK_RESPONSES.tldr
  else if (promptLower.includes('tone'))
    responseText = MOCK_RESPONSES.adjustTone
  const vercelStream = createVercelAIStream(responseText, {
    mode: 'word',
    delay: 80,
    wordsPerChunk: 1,
  })
  return new ReadableStream<Uint8Array>({
    async start(controller) {
      const reader = vercelStream.getReader()
      if (signal?.aborted) {
        controller.close()
        return
      }
      const abortHandler = () => {
        reader.cancel()
        controller.close()
      }
      signal?.addEventListener('abort', abortHandler)
      try {
        const encoder = new TextEncoder()
        const metadata = `f:{"messageId":"mock-${Date.now()}"}\n`
        controller.enqueue(encoder.encode(metadata))
        await new Promise((resolve) => setTimeout(resolve, 200))
        while (true) {
          if (signal?.aborted) break
          const { done, value } = await reader.read()
          if (done) break
          controller.enqueue(value)
        }
        const usage = {
          inputTokens: Math.floor(Math.random() * 100) + 50,
          outputTokens: Math.floor(responseText.length / 4),
        }
        const endLine = `e:{"finishReason":"stop","usage":${JSON.stringify(usage)},"isContinued":false}\n`
        const doneLine = `d:{"finishReason":"stop","usage":${JSON.stringify(usage)}}\n`
        controller.enqueue(encoder.encode(endLine))
        controller.enqueue(encoder.encode(doneLine))
      } finally {
        signal?.removeEventListener('abort', abortHandler)
        reader.releaseLock()
        controller.close()
      }
    },
  });
}

export const Route = createFileRoute('/demo/tiptap/ai-changes')({
  component: AiChangesDemo,
})

function AiChangesDemo() {
  const [state, setState] = useState({
    isLoading: false,
    errorMessage: null as string | null,
    response: null as string | null,
  })
  const [streamMode, setStreamMode] = useState(true)
  const [useMockStream, setUseMockStream] = useState(true)
  const [popoverElement, setPopoverElement] = useState<HTMLElement | null>(null)
  const popoverRef = useRef<HTMLElement | null>(null)

  const initialContent = `\
  <h1>
    Rocking like a mobile?
  </h1>
  <p>
    Did you hear about the mobile phone that joined a rock band? Yeah, it was a real smartTONE!
    It rocked the stage with its gigabytes of rhythm and had everyone calling for an encore, but
    it couldn't resist the temptation to drop a few bars and left the audience in absolute silence.
    Turns out, it wasn't quite cut out for the music industry.
  </p>
  <p>
    They say it's now pursuing a career in ringtone composition. Who knows, maybe one day it'll
    top the charts with its catchy melodies!
  </p>
  `

  const { editor } = useAiEditor({
    changesOptions: {
      popoverRef,
    },
    content: initialContent,
  })

  const previousChangesRef = useRef<any[]>([])

  const isTrackingAiChanges =
    editor?.storage?.aiChanges?.getIsTrackingAiChanges?.() || false
  const changes = editor?.storage?.aiChanges?.getChanges?.() || []

  useEffect(() => {
    if (!editor) return
    if (
      isTrackingAiChanges &&
      changes.length === 0 &&
      previousChangesRef.current.length > 0
    ) {
      editor?.commands?.stopTrackingAiChanges?.()
    }
    previousChangesRef.current = changes
  }, [isTrackingAiChanges, changes, editor])

  useEffect(() => {
    if (!editor) return
    if (!state.isLoading && state.response !== null && isTrackingAiChanges) {
      setTimeout(() => {
        editor?.commands?.setShowAiChanges?.(true)
        const changes = editor.storage?.aiChanges?.getChanges?.() || []
        if (changes.length > 0) {
          editor?.commands?.selectAiChange?.(changes[0].id)
        }
        setState((prev) => ({ ...prev, response: null }))
      }, 500)
    }
  }, [state.isLoading, state.response, isTrackingAiChanges, editor])

  const {
    empty: selectionIsEmpty,
    from: selectionFrom,
    to: selectionTo,
  } = editor?.state.selection || { empty: true, from: 0, to: 0 }
  const selectionContainsText = editor?.state.doc.textBetween(
    selectionFrom,
    selectionTo,
    ' '
  )
  const disableAiGenerationButtons = selectionIsEmpty || !selectionContainsText

  const logPluginState = (label: string) => {
    if (!editor) return
    const pluginState = aiChangesPluginKey.getState(editor.state)
    console.log(`[AIChanges DEMO] ${label}`, pluginState)
  }

  const handleAiCommand = (commandName: string, ...args: any[]) => {
    return () => {
      if (!editor) return
      try {
        logPluginState('Before AI command: Start Tracking Changes')
        editor?.commands?.startTrackingAiChanges?.()
        setTimeout(() => {
          logPluginState('After startTrackingAiChanges')
          const command = commandName.replace(/^aiExtension\./, '')
          const aiCommand = (
            editor.chain()?.setShowAiChanges?.(false) as any
          )?.[command]
          if (typeof aiCommand === 'function') {
            aiCommand({ stream: streamMode, ...(args[0] || {}) }).run()
          } else {
            throw new Error(`AI command ${commandName} not found`)
          }
          editor.view.dispatch(
            editor.state.tr.setMeta(aiChangesPluginKey, {
              type: 'updateChanges',
            })
          )
          setState({ isLoading: false, errorMessage: null, response: null })
          setTimeout(() => {
            logPluginState('After AI command')
          }, 500)
        }, 0)
      } catch (error) {
        console.error(error)
        setState({
          isLoading: false,
          errorMessage:
            error instanceof Error ? error.message : 'Unknown error',
          response: null,
        })
      }
    };
  }

  // Get selected change from plugin state
  const pluginState = editor?.state && aiChangesPluginKey.getState(editor.state)
  const selectedChange = pluginState?.selectedChangeId
    ? pluginState.changes.find(
        (c: any) => c.id === pluginState.selectedChangeId
      ) || null
    : null
  useEffect(() => {
    if (selectedChange && popoverRef.current) {
      setPopoverElement(popoverRef.current)
    } else {
      setPopoverElement(null)
    }
  }, [selectedChange])

  return (
    <div className='flex h-screen flex-row'>
      {/* Left Panel: Controls */}
      <div className='w-1/2 flex-shrink-0 space-y-3 overflow-y-auto border-r border-gray-300 bg-card p-4 dark:bg-gray-900'>
        {/* Reset and Apply All Changes Buttons */}
        <div className='mb-4 flex gap-2'>
          <Button
            onClick={() => {
              editor?.commands.setContent(initialContent)
              setTimeout(() => logPluginState('After Reset'), 100)
            }}
            disabled={!editor}
            className='rounded-sm bg-gray-200 px-3 py-2 text-sm text-gray-800 hover:bg-gray-300 disabled:bg-gray-100'
          >
            Reset
          </Button>
          <Button
            onClick={() => {
              if (editor) {
                logPluginState('Before Apply All Changes')
                editor.commands.acceptAllAiChanges?.()
                setTimeout(() => logPluginState('After Apply All Changes'), 500)
              }
            }}
            disabled={!editor}
            className='rounded-sm bg-status-green px-3 py-2 text-sm text-white hover:bg-green-700 disabled:bg-gray-400'
          >
            Apply All Changes
          </Button>
          <Button
            onClick={() => {
              if (editor) {
                logPluginState('Before Reject All Changes')
                editor.commands.rejectAllAiChanges?.()
                setTimeout(
                  () => logPluginState('After Reject All Changes'),
                  500
                )
              }
            }}
            disabled={!editor}
            className='rounded-sm bg-red-600 px-3 py-2 text-sm text-white hover:bg-red-700 disabled:bg-gray-400'
          >
            Reject All Changes
          </Button>
        </div>
        {/* Status/Loading/Error */}
        {state.errorMessage && (
          <div className='rounded-md border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20'>
            <div className='flex items-center gap-2'>
              <span className='text-lg text-destructive'>❌</span>
              <span className='text-sm text-status-red-emphasis'>
                {state.errorMessage}
              </span>
            </div>
          </div>
        )}
        {state.isLoading && (
          <div className='mb-2 flex items-center gap-2 text-sm text-status-blue dark:text-blue-300'>
            <svg className='h-4 w-4 animate-spin' viewBox='0 0 24 24'>
              <circle
                className='opacity-25'
                cx='12'
                cy='12'
                r='10'
                stroke='currentColor'
                strokeWidth='4'
                fill='none'
              />
              <path
                className='opacity-75'
                fill='currentColor'
                d='M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z'
              />
            </svg>
            AI is generating...
          </div>
        )}
        {/* Mock/Real Toggle */}
        <div className='mb-4 flex items-center gap-4'>
          <label className='flex items-center gap-2'>
            <input
              type='checkbox'
              checked={useMockStream}
              onChange={(e) => setUseMockStream(e.target.checked)}
            />
            <span className='text-sm'>Use Mock Stream</span>
          </label>
          <span className='text-xs text-gray-500'>
            Current: <b>{useMockStream ? 'Mock' : 'Real API'}</b>
          </span>
        </div>
        {/* AI Command Buttons (from ai-demo.tsx) */}
        <div className='button-group mb-4 grid grid-cols-2 gap-2'>
          <Button
            onClick={handleAiCommand('aiRestructure')}
            disabled={disableAiGenerationButtons || state.isLoading}
          >
            Restructure
          </Button>
          <Button
            onClick={handleAiCommand('aiBloggify')}
            disabled={disableAiGenerationButtons || state.isLoading}
          >
            Bloggify
          </Button>
          <Button
            onClick={handleAiCommand('aiKeypoints')}
            disabled={disableAiGenerationButtons || state.isLoading}
          >
            Keypoints
          </Button>
          <Button
            onClick={handleAiCommand('aiRephrase')}
            disabled={disableAiGenerationButtons || state.isLoading}
          >
            Rephrase
          </Button>
          <Button
            onClick={handleAiCommand('aiShorten')}
            disabled={disableAiGenerationButtons || state.isLoading}
          >
            Shorten
          </Button>
          <Button
            onClick={handleAiCommand('aiExtend')}
            disabled={disableAiGenerationButtons || state.isLoading}
          >
            Extend
          </Button>
          <Button
            onClick={handleAiCommand('aiSummarize')}
            disabled={disableAiGenerationButtons || state.isLoading}
          >
            Summarize
          </Button>
          <Button
            onClick={handleAiCommand('aiSimplify')}
            disabled={disableAiGenerationButtons || state.isLoading}
          >
            Simplify
          </Button>
          <Button
            onClick={handleAiCommand('aiFixSpellingAndGrammar')}
            disabled={disableAiGenerationButtons || state.isLoading}
          >
            Fix Spelling
          </Button>
          <Button
            onClick={handleAiCommand('aiComplete', { append: true })}
            disabled={disableAiGenerationButtons || state.isLoading}
          >
            Continue Writing
          </Button>
          <Button
            onClick={handleAiCommand('aiAdjustTone', 'excited')}
            disabled={disableAiGenerationButtons || state.isLoading}
          >
            Excited Tone
          </Button>
          <Button
            onClick={handleAiCommand('aiEmojify')}
            disabled={disableAiGenerationButtons || state.isLoading}
          >
            Enrich with 🙂
          </Button>
          <Button
            onClick={handleAiCommand('aiDeEmojify')}
            disabled={disableAiGenerationButtons || state.isLoading}
          >
            De-Emojify
          </Button>
          <Button
            onClick={handleAiCommand('aiTranslate', 'sv')}
            disabled={disableAiGenerationButtons || state.isLoading}
          >
            Translate to Swedish
          </Button>
          <Button
            onClick={handleAiCommand('aiTranslate', 'de')}
            disabled={disableAiGenerationButtons || state.isLoading}
          >
            Translate to German
          </Button>
          <Button
            onClick={handleAiCommand('aiTldr')}
            disabled={disableAiGenerationButtons || state.isLoading}
          >
            tl;dr
          </Button>
        </div>
        {/* ... any other controls ... */}
      </div>
      {/* Right Panel: Editor only */}
      <div className='flex min-h-[600px] flex-1 items-center justify-center bg-card px-5 dark:bg-gray-900'>
        <EditorContent editor={editor} />
      </div>
      <ReviewChangePopover
        editor={editor}
        element={popoverElement}
        selectedChange={selectedChange}
      />
    </div>
  )
}

export default AiChangesDemo
